import { SITE_LEVEL_PAGE_ID } from '@wix/a11y-wizard-common';
import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { CollapseList, RuleContent, StatusIcon, TaskListItem, useAutoExpandFailedRules } from '@wix/rules-panel-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { AnalysisState, usePanelStateAtom } from '../../../../../hooks/rules-state';
import { useGetRuleIdToLabelMap } from '../../../../../hooks/use-get-ruleId-to-label-map';
import { useIsEditor3 } from '../../../../../hooks/use-is-editor3';
import { useQueryRuleExecutionResultsQuery } from '../../../../../queries/use-query-rule-execution-results-query';
import { resolveGroupAnalysisState } from '../../../../../utils/resolve-group-analysis-state';
import { useSiteLevelRuleResults } from '../../rule-groups/site-level-group/use-site-level-rule-results';
import { useGetPageRuleIds } from '../utils/use-get-page-rule-ids';
import { useShowFetchErrorModal } from '../utils/use-show-fetch-error-modal';
import { CollapseActionsProvider, ItemScopeProvider, useCloseSelf } from './context/collapse-actions-context';
export const SiteLevelTaskList = _ref => {
  let {
    isCardOpen
  } = _ref;
  const showFetchErrorModal = useShowFetchErrorModal();
  const {
    getGroupData,
    setGroupData
  } = usePanelStateAtom();
  const {
    refetch: refetchRuleExecutionResults
  } = useQueryRuleExecutionResultsQuery(SITE_LEVEL_PAGE_ID, () => showFetchErrorModal({
    cancelCallback: () => {
      setGroupData(SITE_LEVEL_PAGE_ID, {
        analysisState: AnalysisState.AnalyzeDonePendingResults
      });
      closeCard();
    },
    tryAgainCallback: refetchRuleExecutionResults
  }));
  const closeCard = useCloseSelf();
  const groupData = getGroupData(SITE_LEVEL_PAGE_ID);
  if (!groupData) {
    throw new Error('Group data not found for site level');
  }
  const {
    analysisState
  } = groupData;
  const {
    isFetchResultSucceeded
  } = resolveGroupAnalysisState(analysisState);
  const ruleResults = useSiteLevelRuleResults();
  const [openItems, setOpenItems] = useState(new Set());

  // Auto-fetch when group is open and results become pending
  useEffect(() => {
    if (isCardOpen && analysisState === AnalysisState.AnalyzeDonePendingResults) {
      refetchRuleExecutionResults();
    }
  }, [analysisState, isCardOpen, refetchRuleExecutionResults]);
  const {
    t
  } = useTranslation();
  const {
    siteLevelruleIdToLabelKeyMap
  } = useGetRuleIdToLabelMap();
  const isEditor3 = useIsEditor3();
  const ruleIdsForSkeleton = useGetPageRuleIds(SITE_LEVEL_PAGE_ID);
  const tasks = useMemo(() => {
    if (!isFetchResultSucceeded) {
      return ruleIdsForSkeleton == null ? void 0 : ruleIdsForSkeleton.map(ruleId => {
        return /*#__PURE__*/React.createElement(ItemScopeProvider, {
          id: ruleId,
          key: ruleId
        }, /*#__PURE__*/React.createElement(TaskListItem, {
          id: ruleId,
          isLoading: true,
          showBottomShadow: !isEditor3,
          showTopShadow: !isEditor3,
          title: t(siteLevelruleIdToLabelKeyMap[ruleId]),
          variant: "collapsible"
        }));
      });
    }
    return ruleResults.map((ruleResult, index, array) => {
      const isLast = index === array.length - 1;
      return /*#__PURE__*/React.createElement(ItemScopeProvider, {
        id: ruleResult.ruleId,
        key: ruleResult.ruleId
      }, /*#__PURE__*/React.createElement(TaskListItem, {
        id: ruleResult.ruleId,
        prefix: /*#__PURE__*/React.createElement(StatusIcon, {
          status: ruleResult.status
        }),
        showBottomShadow: !isLast && !isEditor3,
        showTopShadow: !isEditor3,
        title: ruleResult.label,
        variant: "collapsible"
      }, /*#__PURE__*/React.createElement(Box, {
        dataHook: "site-task-list-item-content",
        padding: "SP3 SP6 SP8 SP6"
      }, /*#__PURE__*/React.createElement(RuleContent, {
        ruleResult: ruleResult
      }))));
    });
  }, [isFetchResultSucceeded, ruleResults, ruleIdsForSkeleton, isEditor3, t, siteLevelruleIdToLabelKeyMap]);
  const ruleResultsForAutoExpand = useMemo(() => {
    return ruleResults.map(result => {
      return {
        id: result.ruleId,
        status: result.status
      };
    });
  }, [ruleResults]);
  useAutoExpandFailedRules({
    items: ruleResultsForAutoExpand,
    setOpenItems
  });
  return /*#__PURE__*/React.createElement(CollapseActionsProvider, {
    setOpenItems: setOpenItems
  }, /*#__PURE__*/React.createElement(CollapseList, {
    contentGap: 0,
    openItems: openItems,
    setOpenItems: setOpenItems
  }, tasks));
};
//# sourceMappingURL=site-level-task-list.js.map